import type { FollowUpView, LocalizedText, OptionView, StoryLanguage } from "./contract";

// The field text the clickable courses of action write (play contract §3.5; agent-service#129, spark#1075). A click on a
// follow-up never sends: it puts the follow-up's template and one space at the START of the field, and the player
// finishes the sentence. These functions are the whole rule for that text, so the component only moves the caret.
//
// "Unchanged" means byte-equal at the start, the one space included: a template whose space was deleted or one of whose
// words was edited is the player's own text from then on, and no later click removes or replaces it. A player who types
// a template's exact words by hand is treated as if they had clicked it, since only the field's text tells.

/** What a click puts in the field: the template and exactly one space, the caret's place after it. */
export function inserted(template: LocalizedText, language: StoryLanguage): string {
  return `${template[language].trimEnd()} `;
}

/** The follow-up whose unchanged template starts the text, the longest such one, or null. */
export function leadingTemplate(text: string, followUps: readonly FollowUpView[], language: StoryLanguage): FollowUpView | null {
  let found: FollowUpView | null = null;
  let length = 0;
  for (const followUp of followUps) {
    const start = inserted(followUp.template, language);
    if (start.length > length && text.startsWith(start)) {
      found = followUp;
      length = start.length;
    }
  }
  return found;
}

/** Every follow-up of the situation, in display order. */
export function followUpsOf(options: readonly OptionView[]): FollowUpView[] {
  return options.flatMap((option) => option.followUps);
}

/**
 * The field after a click on `followUp`, and where the caret goes (UTF-16, for `setSelectionRange`). An unchanged
 * template of any of the situation's follow-ups (`followUps`) at the start is replaced; otherwise the template goes in
 * front of what the player typed, whose leading whitespace is dropped so exactly one space follows the template.
 */
export function fill(
  text: string,
  followUp: FollowUpView,
  followUps: readonly FollowUpView[],
  language: StoryLanguage,
): { text: string; caret: number } {
  const start = inserted(followUp.template, language);
  const leading = leadingTemplate(text, followUps, language);
  const rest = leading ? text.slice(inserted(leading.template, language).length) : text.trimStart();
  return { text: start + rest, caret: start.length };
}

/** The field after „Zurück“ from `option`'s follow-ups: an unchanged template of one of them at the start goes. */
export function unfill(text: string, option: OptionView, language: StoryLanguage): string {
  const leading = leadingTemplate(text, option.followUps, language);
  return leading ? text.slice(inserted(leading.template, language).length) : text;
}

// The characters a blank turn consists of, as `isBlankText` counts them (story.ts): whitespace by JavaScript's `trim`
// and U+001C to U+001F, which Kotlin's `isBlank` counts too.
function isBlankChar(char: string): boolean {
  return char.trim() === "" || (char >= "\u001C" && char <= "\u001F");
}

function stripBlank(text: string): string {
  const chars = Array.from(text);
  let start = 0;
  let end = chars.length;
  while (start < end && isBlankChar(chars[start]!)) start++;
  while (end > start && isBlankChar(chars[end - 1]!)) end--;
  return chars.slice(start, end).join("");
}

/**
 * Whether the text is nothing but one of the situation's templates, in either language, blanks at both ends aside: an
 * empty move, which cannot be sent and which agent-service refuses as `text_required` too.
 */
export function isBareTemplate(text: string, options: readonly OptionView[]): boolean {
  const words = stripBlank(text);
  if (words === "") return false;
  return followUpsOf(options).some((followUp) => stripBlank(followUp.template.de) === words || stripBlank(followUp.template.en) === words);
}
